import { useSettings } from '@/services/settingsService';

/**
 * Central registry of every UI label the Super Admin can rename from the panel.
 * Keys are stable across releases; the "default" is what the app ships with.
 * Grouped for the UI editor — the app itself just calls useLabel(key).
 */
export const LABEL_KEYS = {
  admin: [
    { key: 'admin.dashboard',   default: 'Dashboard' },
    { key: 'admin.orders',      default: 'Pedidos' },
    { key: 'admin.products',    default: 'Productos' },
    { key: 'admin.stores',      default: 'Tiendas' },
    { key: 'admin.customers',   default: 'Clientes' },
    { key: 'admin.loyalty',     default: 'Sellos' },
    { key: 'admin.abandoned',   default: 'Carritos' },
    { key: 'admin.reports2',    default: 'Exportar' },
    { key: 'admin.users',       default: 'Usuarios' },
    { key: 'admin.satisfaction',default: 'Satisfacción' },
    { key: 'admin.settings',    default: 'Tasas' },
    { key: 'admin.labels',      default: 'Personalizar Etiquetas' },
  ],
  store: [
    { key: 'store.orders',      default: 'Pedidos' },
    { key: 'store.loyalty',     default: 'Sellos' },
  ],
  category: [
    { key: 'category.relojeria',    default: 'Relojería' },
    { key: 'category.belleza',      default: 'Belleza' },
    { key: 'category.bolsos',       default: 'Bolsos' },
    { key: 'category.bandoleros',   default: 'Bandoleros' },
    { key: 'category.bisuteria',    default: 'Bisutería' },
    { key: 'category.accesorios',   default: 'Accesorios' },
    { key: 'category.llaveros',     default: 'Llaveros' },
    { key: 'category.electronicos', default: 'Electrónicos' },
    { key: 'category.papeleria',    default: 'Papelería' },
    { key: 'category.skincare',     default: 'Skincare' },
    { key: 'category.capilares',    default: 'Productos Capilares' },
  ],
  status: [
    { key: 'status.Recibido',              default: 'Recibido' },
    { key: 'status.Pago en Verificación',  default: 'Pago en Verificación' },
    { key: 'status.En Preparación',        default: 'En Preparación' },
    { key: 'status.Enviado',               default: 'Enviado' },
    { key: 'status.Listo para Retiro',     default: 'Listo para Retiro' },
    { key: 'status.Entregado',             default: 'Entregado' },
    { key: 'status.Cancelado',             default: 'Cancelado' },
  ],
  buttons: [
    { key: 'btn.add-to-cart',   default: 'Agregar al carrito' },
    { key: 'btn.buy-whatsapp',  default: 'Comprar por WhatsApp' },
    { key: 'btn.view-details',  default: 'Ver detalle' },
    { key: 'btn.all-products',  default: 'Todos los productos' },
    { key: 'btn.checkout',      default: 'Finalizar pedido' },
    { key: 'btn.my-account',    default: 'Mi cuenta / Sellos' },
    { key: 'btn.my-favorites',  default: 'Mis favoritos' },
  ],
};

const ALL = [
  ...LABEL_KEYS.admin,
  ...LABEL_KEYS.store,
  ...LABEL_KEYS.category,
  ...LABEL_KEYS.status,
  ...LABEL_KEYS.buttons,
];
const DEFAULTS = Object.fromEntries(ALL.map((l) => [l.key, l.default]));

/**
 * Hook: returns a `label(key, fallback?)` function that resolves the rename
 * defined by the Super Admin in the "Personalizar Etiquetas" tab, or falls
 * back to the shipped default. Any component can call it and it will
 * re-render whenever the settings are reloaded.
 */
export function useLabels() {
  const { settings } = useSettings();
  const custom = (settings && settings.custom_labels) || {};
  const label = (key, fallback) => {
    const v = (custom[key] || '').trim();
    if (v) return v;
    return fallback ?? DEFAULTS[key] ?? key;
  };
  return { label, defaults: DEFAULTS };
}
